/**
 * THE TICKET PAGE'S DECISIONS, in one place and free of React: who is
 * looking, which tabs they get, where the ticket stands, what its header
 * offers. The page (TicketLayout and its tabs) only draws what these say,
 * so a rule is changed here once rather than in three page builds - which is
 * how the staff page, the contractor page and the work order page drifted
 * apart in the first place.
 *
 * Roles. The server says who is looking (GET /tickets/:id `viewer`,
 * lib/ticket-viewer.js); an older server - or a test mocking the API -
 * sends none, so this reads the same answer off /auth/me and the ticket's
 * own columns, which gets everything but the IT-staff and private-note
 * parts. Never the protection: every write is decided by its own route.
 */
import { invoiceOf } from '../../lib/ticketStage';
import { relative, timeLabel } from '../../lib/when';

const norm = (v) => String(v || '').trim().toLowerCase();

export const statusOf = (t) => norm(t?.status_name || t?.status);
export const isSettled = (t) => Boolean(t?.is_locked)
  || ['approved', 'closed'].includes(statusOf(t));

/** 'admin' | 'worker' | 'requester' | 'assignee' | 'staff' | 'contractor' | 'raiser'. */
export function roleOf({ me, ticket, contractor = null }) {
  const v = ticket?.viewer;
  if (v && v.role) return v.role;
  if (contractor) {
    const mine = contractor.id ? String(contractor.id) : null;
    const raised = mine && String(ticket?.requester_contractor_id || '') === mine;
    const assigned = mine && String(ticket?.contractor_id || '') === mine;
    return raised && !assigned ? 'raiser' : 'contractor';
  }
  if (me?.isAdmin) return 'admin';
  if (me?.id && String(ticket?.requester_id || '') === String(me.id)) return 'requester';
  if (me?.id && String(ticket?.assigned_to || '') === String(me.id)) return 'assignee';
  return 'staff';
}

/** What this person may do here, from the server's block or the same reading. */
export function abilitiesOf({ me, ticket, role }) {
  const v = ticket?.viewer || {};
  const isAdmin = role === 'admin';
  // Sent back for more information (087) is still undecided: the approver
  // keeps the card, showing what they asked.
  const approval = norm(ticket?.approval_status || 'pending');
  const pending = ['pending', 'sent_back'].includes(approval) && statusOf(ticket) === 'awaiting approval';
  return {
    canEdit: v.can_edit ?? isAdmin,
    canWork: v.can_work ?? (isAdmin || role === 'worker' || role === 'contractor'),
    canDecide: v.can_decide ?? (isAdmin && Boolean(me?.isApprover) && pending),
    // Answering the approver's question and putting it back in their queue.
    canResubmit: v.can_resubmit ?? (approval === 'sent_back' && ['requester', 'raiser', 'admin'].includes(role)),
    privateOn: Boolean(v.private_notes_on),
    canReadPrivate: Boolean(v.can_read_private),
    canWritePrivate: Boolean(v.can_write_private),
    // The SLA read is staff-only and the requester is spared its internals.
    seesSla: ['admin', 'worker', 'assignee', 'staff'].includes(role),
    seesHistory: ['admin', 'worker', 'assignee', 'staff'].includes(role)
  };
}

/**
 * THE TABS. Sub-routes under /tickets/:id (and /my-tickets/:id for a
 * contractor), drawn as one row. The requester gets the calm two-tab view
 * (Gordon's decision 4); a contractor their job's tabs without History,
 * which the server does not serve them.
 */
export const TAB_DEFS = [
  { key: 'conversation', path: '', label: 'Conversation', short: 'c' },
  { key: 'work', path: 'work', label: 'Work & costs', short: 'w' },
  { key: 'device', path: 'device', label: 'Device', short: 'd' },
  { key: 'docs', path: 'docs', label: 'Docs & to-dos', short: 'o' },
  { key: 'files', path: 'files', label: 'Files', short: 'f' },
  { key: 'history', path: 'history', label: 'History', short: 'h' }
];

export function tabsFor(role, { todosOn = true, assetsOn = true } = {}) {
  const keys = {
    requester: ['conversation', 'files'],
    raiser: ['conversation', 'files'],
    staff: ['conversation', 'files', 'history'],
    assignee: ['conversation', 'device', 'docs', 'files', 'history'],
    admin: ['conversation', 'work', 'device', 'docs', 'files', 'history'],
    worker: ['conversation', 'work', 'device', 'docs', 'files', 'history'],
    contractor: ['conversation', 'work', 'device', 'docs', 'files']
  }[role] || ['conversation', 'files'];
  return TAB_DEFS.filter((t) => keys.includes(t.key) && (assetsOn || t.key !== 'device'))
    .map((t) => (t.key === 'docs' && !todosOn ? { ...t, label: 'Docs' } : t));
}

/** The tab a path names: '/tickets/x/work' -> 'work'. */
export function tabFromPath(pathname) {
  const last = String(pathname || '').replace(/\/+$/, '').split('/').pop();
  const hit = TAB_DEFS.find((t) => t.path && t.path === last);
  if (hit) return hit.key;
  if (last === 'details') return 'details';
  return 'conversation';
}

/* ------------------------------------------------------------- the stage */

const STAFF_STEPS = ['Raised', 'Approved', 'In progress', 'Resolved', 'Signed off', 'Billed'];
const PLAIN_STEPS = ['Sent', 'Approved', 'Being fixed', 'Fixed'];

/**
 * Where it is, as a stepper. `at` is the last step reached; a ticket that
 * was turned down has no place on the line, and says so instead.
 */
export function stepsFor(ticket, { plain = false } = {}) {
  const s = statusOf(ticket);
  const rejected = ['rejected', 'declined'].includes(norm(ticket?.approval_status));
  const invoiced = Boolean(invoiceOf(ticket));
  let at = 0;
  if (s === 'open') at = 1;
  else if (s === 'in progress' || s === 'on hold') at = 2;
  else if (s === 'resolved') at = 3;
  else if (s === 'approved') at = 4;
  else if (s === 'closed') at = invoiced ? 5 : 4;
  const steps = plain ? PLAIN_STEPS : STAFF_STEPS;
  if (plain) at = Math.min(at, 3);
  return { steps, at, rejected, paused: s === 'on hold' };
}

/** A status chip's tone: one of the app's badge tones. */
export const statusTone = (t) => ({
  'awaiting approval': 'purple', open: 'blue', 'in progress': 'amber', 'on hold': 'grey',
  resolved: 'green', approved: 'green', closed: 'grey'
}[statusOf(t)] || 'grey');

export const priorityTone = (p) => ({ critical: 'red', urgent: 'red', high: 'red', medium: 'amber', low: 'grey' }[norm(p)] || 'grey');

/* -------------------------------------------------------------- the SLA */

/**
 * The SLA as one chip, only while its clock is running (resolved_at unset).
 * It reads the resolution clock GET /tickets/:id/sla returns. The old box
 * read fields that route has never sent and, after approval, still printed
 * OVERDUE about the approval wait; a decided ticket's chip is about getting
 * it fixed, and an undecided one says it is waiting rather than late.
 *
 * WORKING HOURS (114). The server counts the time left on the ticket's SLA
 * profile - Office hours, 24/7, a client's contract - and sends it as
 * working_hours_remaining, so the chip says "due in 5 working hours" rather
 * than dividing the wall-clock gap to the deadline, which over a weekend
 * would read 60 hours for a ticket with one working hour left. A server from
 * before 114 sends no such figure, and the chip reads the clock as it did.
 */
const workingSpan = (h, always) => {
  const a = Math.abs(h);
  const unit = always ? '' : ' working';
  if (a < 1) {
    const m = Math.max(1, Math.round(a * 60));
    return `${m}${unit} min`;
  }
  const n = a < 10 ? Math.round(a * 10) / 10 : Math.round(a);
  return `${n}${unit} hour${n === 1 ? '' : 's'}`;
};

/** "Mon 2 Nov, 08:00" - when a stopped clock starts again. */
const resumeWords = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}, ${timeLabel(d)}`;
};

/** The SLA profile, in a sentence: "Office hours (Mon–Fri 08:00–17:00 · public holidays off)". */
export const slaProfileWords = (sla) => {
  const p = sla?.profile;
  if (!p || !p.name) return '';
  return p.summary && p.summary !== p.name ? `${p.name} (${p.summary})` : p.name;
};

export function slaChip(sla, ticket, now = new Date()) {
  if (!sla || typeof sla !== 'object' || !sla.resolution_deadline) return null;
  if (sla.resolved_at || ticket?.resolved_at) return null;
  if (isSettled(ticket) || ['resolved'].includes(statusOf(ticket))) return null;
  const profile = slaProfileWords(sla);
  const on = profile ? ` On ${profile}.` : '';
  // Sent back for more information (087): the clock is stopped, because the
  // ticket is waiting on the person who raised it, not on IT.
  if (sla.paused || isSentBack(ticket)) {
    return { tone: 'grey', text: 'SLA paused',
      title: `The fix-by clock is stopped while the requester is asked for more information.${on}` };
  }
  const due = new Date(sla.resolution_deadline);
  if (Number.isNaN(due.getTime())) return null;
  const working = Number.isFinite(Number(sla.working_hours_remaining)) && sla.working_hours_remaining !== null;
  const always = !sla.profile || sla.profile.always_open === true;
  const hours = working ? Number(sla.working_hours_remaining) : (due - now) / 3600000;
  const span = (h) => {
    if (working) return workingSpan(h, always);
    const a = Math.abs(h);
    if (a < 1) return `${Math.max(1, Math.round(a * 60))} min`;
    if (a < 48) return `${Math.round(a)}h`;
    return `${Math.round(a / 24)} days`;
  };
  const exact = `${due.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} ${timeLabel(due)}`;
  // Out of hours the clock is not moving; say when it starts again.
  const stopped = working && sla.clock_running === false && sla.resumes_at
    ? ` The clock is stopped until ${resumeWords(sla.resumes_at)}.` : '';
  if (statusOf(ticket) === 'awaiting approval') {
    return { tone: hours < 0 ? 'amber' : 'grey', text: hours < 0 ? 'Waiting for a decision' : `SLA due in ${span(hours)}`,
      title: `Fix-by ${exact}. The clock runs from when it was raised.${on}${stopped}` };
  }
  if (hours < 0) return { tone: 'red', text: `SLA overdue ${span(hours)}`, title: `Was due ${exact}.${on}` };
  if (hours < 8) return { tone: 'amber', text: `SLA due in ${span(hours)}`, title: `Due ${exact}.${on}${stopped}` };
  return { tone: 'grey', text: `SLA due ${exact}`,
    title: `Due ${exact}${working ? ` - ${span(hours)} from now` : ''}.${on}${stopped}` };
}

/**
 * The SLA on the Details card (114): which working hours this ticket runs on,
 * why, and where its clock stands in those hours. Null when there is nothing
 * to say (no SLA, or a server from before 114 that names no profile).
 */
export function slaFact(sla) {
  if (!sla || typeof sla !== 'object' || !sla.profile?.name) return null;
  const p = sla.profile;
  const always = p.always_open === true;
  const why = {
    default: 'the default',
    category: p.matched?.category ? `for ${p.matched.category} tickets` : 'by category',
    company: p.matched?.company ? `for ${p.matched.company}` : 'by company',
    'category+company': [p.matched?.category, p.matched?.company].filter(Boolean).join(' at ') || 'by category and company',
    'before-114': 'raised before working hours applied'
  }[p.matched?.kind] || null;
  let state = null;
  const h = Number(sla.working_hours_remaining);
  if (!sla.resolved_at && Number.isFinite(h) && sla.working_hours_remaining !== null) {
    if (sla.paused) state = 'paused while the requester is asked';
    else if (h < 0) state = `overdue by ${workingSpan(h, always)}`;
    else state = `due in ${workingSpan(h, always)}`;
    if (!sla.paused && sla.clock_running === false && sla.resumes_at) state += ` · clock resumes ${resumeWords(sla.resumes_at)}`;
  }
  return { name: p.name, summary: p.summary || '', why, state };
}

/* ---------------------------------------------------- where it stands */

const firstName = (n) => String(n || '').trim().split(/\s+/)[0] || '';

/** Sent back to the requester for more information (087), and not answered yet. */
export const isSentBack = (t) => norm(t?.approval_status) === 'sent_back';

/** The approver's open question, when there is one: { question, asked_by_name, asked_at }. */
export const openQuestion = (t) => (isSentBack(t) && t?.sendback && t.sendback.open !== false ? t.sendback : null);

export const whenWords = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}, ${timeLabel(d)}`;
};

/**
 * One plain sentence for the person who raised it: who has it, what is
 * next, whether anything is needed from them. `tone` picks the strip's
 * colour; `ask` is true when the next move is theirs.
 */
export function whereItStands(ticket, summary) {
  const s = statusOf(ticket);
  const approval = norm(ticket?.approval_status);
  const who = [ticket?.assigned_to_name, ticket?.contractor_name].filter(Boolean).map(firstName);
  const people = who.length === 0 ? 'IT' : who.length === 1 ? who[0] : `${who[0]} and ${who[1]}`;
  const visit = summary?.next_visit;
  const visitLine = visit ? ` Next visit ${whenWords(visit.starts_at)}${visit.location ? `, ${visit.location}` : ''}.` : '';
  if (['rejected', 'declined'].includes(approval)) {
    return { tone: 'red', text: `Not approved${ticket?.rejection_reason ? `: “${ticket.rejection_reason}”` : '.'} Reply below if you want to ask again.` };
  }
  if (s === 'awaiting approval' && isSentBack(ticket)) {
    const by = ticket?.sendback?.asked_by_name || ticket?.approver_name;
    return { tone: 'amber', ask: true,
      text: `${by ? firstName(by) : 'Your approver'} needs more information before approving it. Answer above and it goes straight back to them.` };
  }
  if (s === 'awaiting approval') {
    const answered = ticket?.sendback && ticket.sendback.answered_at;
    return { tone: 'blue', text: `${answered ? 'You answered. ' : ''}Waiting for ${ticket?.approver_name ? firstName(ticket.approver_name) : 'an approver'} to approve it. Nothing is needed from you right now.` };
  }
  if (s === 'open') return { tone: 'blue', text: `Approved. ${people === 'IT' ? 'IT will pick it up' : `${people} will pick it up`} soon.${visitLine}` };
  if (s === 'in progress') return { tone: 'blue', text: `${people} ${who.length > 1 ? 'are' : 'is'} working on it.${visitLine} Nothing is needed from you right now.` };
  if (s === 'on hold') return { tone: 'amber', text: `On hold for now.${visitLine} ${people} will say here when it moves again.` };
  if (s === 'resolved') {
    const a = summary?.answer?.answer;
    if (a === 'fixed') return { tone: 'green', text: 'You said it’s fixed. Thank you. IT will close it.' };
    if (a === 'broken') return { tone: 'amber', text: 'You said it’s still broken. IT has been told and will pick it up again.' };
    return { tone: 'green', text: `${people} marked this as fixed. Is it?`, ask: true };
  }
  return { tone: 'grey', text: 'This is done and closed.' };
}

/* -------------------------------------------------------- the timeline */

/** What kind of line an activity row is, for the filter chips. */
export function kindOf(row) {
  if (row?.action === 'commented' || (row?.comment && String(row.comment).trim())) return 'message';
  const r = String(row?.reason || row?.description || '');
  if (/^Session ended/i.test(r) || /\bcall\b/i.test(r)) return 'call';
  if (/^Appointment /i.test(r) || /\bvisit\b/i.test(r)) return 'visit';
  return 'change';
}

/** The small label beside a name: who they are on this ticket. */
export function authorRole(c, ticket) {
  if (c?.author_contractor || c?.acted_by_contractor || c?.actor_is_contractor) {
    if (ticket?.requester_contractor_id && String(c.acted_by_contractor) === String(ticket.requester_contractor_id)) return 'Requester';
    return 'Contractor';
  }
  const u = c?.user_id ? String(c.user_id) : null;
  if (u && u === String(ticket?.requester_id || '')) return 'Requester';
  // The approver, unless they are also the one it is assigned to - then
  // they are writing as IT, which is the job they are doing.
  if (u && u === String(ticket?.approver_id || '') && u !== String(ticket?.assigned_to || '')) return 'Approver';
  return 'IT';
}

export const authorName = (c) => c?.author_name || c?.author_contractor || c?.actor_name || 'Somebody';

export const stamp = (iso) => {
  const d = new Date(iso);
  if (!iso || Number.isNaN(d.getTime())) return '';
  return `${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}, ${timeLabel(d)}`;
};
export const ago = (iso) => relative(iso);

export const hrs = (n) => `${Number(n || 0).toFixed(2).replace(/\.?0+$/, '')}h`;
export const euro = (n) => `€${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
